/**
 * Transform handle geometry and drag math for the CanvasKit editor.
 * Pure functions -- no side effects or CanvasKit dependency.
 */

// ─── Types ────────────────────────────────────────────────────────────────

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rotate';

export interface Point {
  x: number;
  y: number;
}

export interface WorldBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface TransformResult {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────

const HANDLE_SIZE = 8; // px in screen space
const HANDLE_HIT_AREA = 12; // px in screen space (larger for easy targeting)
const ROTATION_HANDLE_OFFSET = 24; // px above node in screen space
const MIN_NODE_SIZE = 5;

// ─── Rotation helpers ─────────────────────────────────────────────────────

function rotatePoint(px: number, py: number, cx: number, cy: number, angleDeg: number): Point {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = px - cx;
  const dy = py - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

// ─── Handle positions ─────────────────────────────────────────────────────

/**
 * Compute the 8 resize handle positions and 1 rotation handle position
 * in world coordinates (scene space).
 */
export function getHandlePositions(bounds: WorldBounds): Record<HandleId, Point> {
  const { x, y, width, height, rotation } = bounds;
  const cx = x + width / 2;
  const cy = y + height / 2;

  // Unrotated positions
  const raw: Record<HandleId, Point> = {
    nw: { x, y },
    n: { x: x + width / 2, y },
    ne: { x: x + width, y },
    e: { x: x + width, y: y + height / 2 },
    se: { x: x + width, y: y + height },
    s: { x: x + width / 2, y: y + height },
    sw: { x, y: y + height },
    w: { x, y: y + height / 2 },
    rotate: { x: x + width / 2, y: y - ROTATION_HANDLE_OFFSET },
  };

  // Apply rotation around center
  if (rotation) {
    for (const key of Object.keys(raw) as HandleId[]) {
      raw[key] = rotatePoint(raw[key].x, raw[key].y, cx, cy, rotation);
    }
  }

  return raw;
}

export { HANDLE_SIZE, HANDLE_HIT_AREA, ROTATION_HANDLE_OFFSET };

// ─── Handle availability ──────────────────────────────────────────────────

/** Structural subset of SceneNode that determines which handles apply. */
export interface HandleSizingInfo {
  layoutSizingHorizontal?: string;
  layoutSizingVertical?: string;
  pluginData?: Record<string, unknown> | null;
}

const ALL_HANDLES: HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w', 'rotate'];

/**
 * Which handles are meaningful for a node. HUG axes derive their size from
 * children, so their handles are dropped (drawing them would lie about what
 * dragging does — and grabbing one would silently flip the axis to FIXED).
 * Hugging artboards (email pages) have no manual transform at all: height is
 * layout-owned, width is a document property, rotation is meaningless.
 */
export function getEnabledHandles(node: HandleSizingInfo | null | undefined): HandleId[] {
  if (!node) return ALL_HANDLES;
  const hugW = node.layoutSizingHorizontal === 'HUG';
  const hugH = node.layoutSizingVertical === 'HUG';
  if (hugH && node.pluginData?.['isArtboard'] === true) return [];
  if (!hugW && !hugH) return ALL_HANDLES;
  return ALL_HANDLES.filter((id) => {
    if (hugW && (id === 'e' || id === 'w')) return false;
    if (hugH && (id === 'n' || id === 's')) return false;
    if (hugW && hugH && (id === 'nw' || id === 'ne' || id === 'se' || id === 'sw')) {
      return false;
    }
    return true;
  });
}

// ─── Handle hit testing ───────────────────────────────────────────────────

/**
 * Check if a scene-space point hits any handle.
 * The hit area is HANDLE_HIT_AREA pixels in screen space.
 * Pass `enabled` (from getEnabledHandles) to skip handles that are not shown.
 */
export function hitTestHandle(
  scenePoint: Point,
  handles: Record<HandleId, Point>,
  zoom: number,
  enabled: HandleId[] = ALL_HANDLES,
): HandleId | null {
  const threshold = HANDLE_HIT_AREA / zoom;

  // Test rotation handle first (smaller target, should take priority)
  if (enabled.includes('rotate')) {
    const rotDist = Math.hypot(
      scenePoint.x - handles.rotate.x,
      scenePoint.y - handles.rotate.y,
    );
    if (rotDist <= threshold) return 'rotate';
  }

  // Test resize handles
  const resizeHandles: HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  for (const id of resizeHandles) {
    if (!enabled.includes(id)) continue;
    const dist = Math.hypot(
      scenePoint.x - handles[id].x,
      scenePoint.y - handles[id].y,
    );
    if (dist <= threshold) return id;
  }

  return null;
}

// ─── Corner radius handles (Figma-style inset circles) ───────────────────

export type RadiusHandleId = 'radius-tl' | 'radius-tr' | 'radius-br' | 'radius-bl';

export const RADIUS_HANDLE_IDS: RadiusHandleId[] = [
  'radius-tl',
  'radius-tr',
  'radius-br',
  'radius-bl',
];

const RADIUS_HANDLE_INSET = 16; // px in screen space from the corner at radius 0
const RADIUS_HANDLE_HIT_AREA = 10; // px in screen space
const RADIUS_HANDLES_MIN_SIDE = 56; // hide on nodes smaller than this on screen

export { RADIUS_HANDLE_INSET };

/** Structural subset of SceneNode the radius handles read. */
export interface RadiusNodeInfo {
  type?: string;
  cornerRadius?: unknown;
  cornerRadii?: unknown;
  fills?: unknown;
  clipContent?: unknown;
}

// RECTANGLE/IMAGE always rasterize with their corner radius. Containers only
// do so through a clip or a self-fill, so they are gated separately below.
const SHAPE_RADIUS_TYPES = new Set(['RECTANGLE', 'IMAGE']);
const CONTAINER_RADIUS_TYPES = new Set(['FRAME', 'GROUP']);

/**
 * Whether the radius handles would actually change anything visible. The
 * renderer only honours a FRAME/GROUP radius when the container clips its
 * children or paints its own fill; on a pure layout frame (background drawn by
 * a child) the radius is a no-op, so we hide the handles rather than show dead
 * affordances.
 */
export function supportsRadiusHandles(node: RadiusNodeInfo | null | undefined): boolean {
  if (!node?.type) return false;
  if (SHAPE_RADIUS_TYPES.has(node.type)) return true;
  if (CONTAINER_RADIUS_TYPES.has(node.type)) {
    if (node.clipContent === true) return true;
    return Array.isArray(node.fills) && node.fills.some((f) => (f as { visible?: boolean })?.visible !== false);
  }
  return false;
}

/**
 * Hide the radius circles when the node is small on screen -- they would sit
 * on top of the resize handles and read as noise (Figma does the same).
 */
export function shouldShowRadiusHandles(bounds: WorldBounds, zoom: number): boolean {
  return Math.min(bounds.width, bounds.height) * zoom >= RADIUS_HANDLES_MIN_SIDE;
}

/** Current per-corner radii [tl, tr, br, bl]; merge-tag strings read as 0. */
export function nodeCornerRadii(node: RadiusNodeInfo | null | undefined): [number, number, number, number] {
  const radii = node?.cornerRadii;
  if (Array.isArray(radii) && radii.length === 4 && radii.every((r) => typeof r === 'number')) {
    return [radii[0], radii[1], radii[2], radii[3]];
  }
  const r = typeof node?.cornerRadius === 'number' ? node.cornerRadius : 0;
  return [r, r, r, r];
}

/**
 * Positions of the 4 radius handles in world (scene) space. Each sits on its
 * corner's diagonal, `max(radius, base inset)` in from the corner -- so the
 * handle tracks the rounded edge once a radius is applied, exactly like
 * Figma's affordance. Capped at the half-min-side so handles never cross.
 */
export function getRadiusHandlePositions(
  bounds: WorldBounds,
  radii: [number, number, number, number],
  zoom: number,
): Record<RadiusHandleId, Point> {
  const { x, y, width, height, rotation } = bounds;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const minInset = RADIUS_HANDLE_INSET / zoom;
  const maxInset = Math.min(width, height) / 2;
  const inset = (r: number) => Math.min(Math.max(r, minInset), maxInset);

  const [tl, tr, br, bl] = radii;
  const raw: Record<RadiusHandleId, Point> = {
    'radius-tl': { x: x + inset(tl), y: y + inset(tl) },
    'radius-tr': { x: x + width - inset(tr), y: y + inset(tr) },
    'radius-br': { x: x + width - inset(br), y: y + height - inset(br) },
    'radius-bl': { x: x + inset(bl), y: y + height - inset(bl) },
  };

  if (rotation) {
    for (const key of RADIUS_HANDLE_IDS) {
      raw[key] = rotatePoint(raw[key].x, raw[key].y, cx, cy, rotation);
    }
  }
  return raw;
}

/** Hit test the radius handles (screen-space threshold, like hitTestHandle). */
export function hitTestRadiusHandle(
  scenePoint: Point,
  handles: Record<RadiusHandleId, Point>,
  zoom: number,
): RadiusHandleId | null {
  const threshold = RADIUS_HANDLE_HIT_AREA / zoom;
  for (const id of RADIUS_HANDLE_IDS) {
    const dist = Math.hypot(scenePoint.x - handles[id].x, scenePoint.y - handles[id].y);
    if (dist <= threshold) return id;
  }
  return null;
}

/**
 * Radius implied by a drag point: its distance in from the corner's two edges
 * (the smaller of the two, Figma semantics), in the node's local space.
 * Clamped to [0, half the short side].
 */
export function computeCornerRadiusFromPoint(
  handle: RadiusHandleId,
  bounds: WorldBounds,
  scenePoint: Point,
): number {
  const { x, y, width, height, rotation } = bounds;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const local = rotation
    ? rotatePoint(scenePoint.x, scenePoint.y, cx, cy, -rotation)
    : scenePoint;
  const px = local.x - x;
  const py = local.y - y;

  let d: number;
  switch (handle) {
    case 'radius-tl': d = Math.min(px, py); break;
    case 'radius-tr': d = Math.min(width - px, py); break;
    case 'radius-br': d = Math.min(width - px, height - py); break;
    case 'radius-bl': d = Math.min(px, height - py); break;
    default: d = 0; break;
  }
  return Math.round(Math.min(Math.max(d, 0), Math.min(width, height) / 2));
}

// ─── Resize computation ───────────────────────────────────────────────────

/**
 * Compute new bounds after dragging a resize handle.
 *
 * For rotated nodes, we work in the node's local coordinate system:
 * 1. Transform mouse positions into the node's local space
 * 2. Compute resize in local space (simple axis-aligned resize)
 * 3. Transform the result back to world space
 */
export function computeResize(
  handle: HandleId,
  startBounds: WorldBounds,
  startMouse: Point,
  currentMouse: Point,
  shiftKey: boolean,
  altKey = false,
): TransformResult {
  const { x, y, width, height, rotation } = startBounds;
  const cx = x + width / 2;
  const cy = y + height / 2;

  // Transform mouse positions to local (un-rotated) space
  const localStart = rotation
    ? rotatePoint(startMouse.x, startMouse.y, cx, cy, -rotation)
    : startMouse;
  const localCurrent = rotation
    ? rotatePoint(currentMouse.x, currentMouse.y, cx, cy, -rotation)
    : currentMouse;

  const dx = localCurrent.x - localStart.x;
  const dy = localCurrent.y - localStart.y;

  let newX = x;
  let newY = y;
  let newW = width;
  let newH = height;

  // Apply resize based on handle
  switch (handle) {
    case 'nw':
      newX = x + dx;
      newY = y + dy;
      newW = width - dx;
      newH = height - dy;
      break;
    case 'n':
      newY = y + dy;
      newH = height - dy;
      break;
    case 'ne':
      newY = y + dy;
      newW = width + dx;
      newH = height - dy;
      break;
    case 'e':
      newW = width + dx;
      break;
    case 'se':
      newW = width + dx;
      newH = height + dy;
      break;
    case 's':
      newH = height + dy;
      break;
    case 'sw':
      newX = x + dx;
      newW = width - dx;
      newH = height + dy;
      break;
    case 'w':
      newX = x + dx;
      newW = width - dx;
      break;
  }

  // Enforce minimum size
  if (newW < MIN_NODE_SIZE) {
    if (handle.includes('w')) {
      newX = x + width - MIN_NODE_SIZE;
    }
    newW = MIN_NODE_SIZE;
  }
  if (newH < MIN_NODE_SIZE) {
    if (handle.includes('n')) {
      newY = y + height - MIN_NODE_SIZE;
    }
    newH = MIN_NODE_SIZE;
  }

  // Shift: maintain aspect ratio
  if (shiftKey && width > 0 && height > 0) {
    const aspectRatio = width / height;
    const isCorner = ['nw', 'ne', 'se', 'sw'].includes(handle);
    const isHorizontal = handle === 'e' || handle === 'w';

    if (isCorner || isHorizontal) {
      const constrainedH = newW / aspectRatio;
      if (handle === 'nw' || handle === 'ne') {
        newY = y + height - constrainedH;
      }
      newH = constrainedH;
    } else {
      const constrainedW = newH * aspectRatio;
      if (handle === 'n' || handle === 's') {
        // Keep centered horizontally for edge handles
      }
      newW = constrainedW;
    }
  }

  // Alt: resize symmetrically about the node center. Each dragged edge's
  // movement is mirrored onto the opposite edge (double the delta, re-center),
  // so the center stays pinned. Runs after Shift's aspect lock, so doubling
  // both dimensions preserves the locked ratio. cx/cy are the local (un-
  // rotated) center — the same space newX/newW live in here.
  if (altKey) {
    const affectsW = handle !== 'n' && handle !== 's';
    const affectsH = handle !== 'e' && handle !== 'w';
    if (affectsW) {
      newW = Math.max(MIN_NODE_SIZE, width + (newW - width) * 2);
      newX = cx - newW / 2;
    }
    if (affectsH) {
      newH = Math.max(MIN_NODE_SIZE, height + (newH - height) * 2);
      newY = cy - newH / 2;
    }
  }

  // If rotated, we need to adjust the position because
  // the resize was computed in un-rotated space
  if (rotation) {
    // Compute new center in local space
    const newCx = newX + newW / 2;
    const newCy = newY + newH / 2;
    // Transform new center back to world space
    const worldCenter = rotatePoint(newCx, newCy, cx, cy, rotation);
    // Adjust position so the center is correct
    newX = worldCenter.x - newW / 2;
    newY = worldCenter.y - newH / 2;
  }

  return { x: newX, y: newY, width: newW, height: newH };
}

// ─── Multi-select scale ───────────────────────────────────────────────────

export interface RectBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Map one node's world bounds through a group-AABB resize. Given the combined
 * selection box before (`startAABB`) and after (`newAABB`) a handle drag, scale
 * the node's position and size proportionally so it keeps its relative place and
 * proportion inside the box — Figma's multi-select resize. Font sizes and stroke
 * weights are intentionally left untouched by the caller (this is Resize, not
 * the Scale tool). Pure so the group-scale math can be unit-tested.
 */
export function scaleNodeBoundsInAABB(
  bounds: RectBounds,
  startAABB: RectBounds,
  newAABB: RectBounds,
): RectBounds {
  const sx = startAABB.width !== 0 ? newAABB.width / startAABB.width : 1;
  const sy = startAABB.height !== 0 ? newAABB.height / startAABB.height : 1;
  return {
    x: newAABB.x + (bounds.x - startAABB.x) * sx,
    y: newAABB.y + (bounds.y - startAABB.y) * sy,
    width: bounds.width * sx,
    height: bounds.height * sy,
  };
}

// ─── Rotation computation ─────────────────────────────────────────────────

/**
 * Compute the new rotation angle when dragging the rotation handle.
 */
export function computeRotation(
  nodeCenter: Point,
  currentMouse: Point,
  startAngle: number,
  shiftKey: boolean,
): number {
  const angle = Math.atan2(
    currentMouse.y - nodeCenter.y,
    currentMouse.x - nodeCenter.x,
  );
  // Convert to degrees, offset by 90 (handle is above center)
  let degrees = (angle * 180) / Math.PI + 90;

  // Adjust relative to start angle
  degrees = degrees - startAngle;

  // Normalize to -180..180
  while (degrees > 180) degrees -= 360;
  while (degrees < -180) degrees += 360;

  if (shiftKey) {
    // Shift: snap to 15-degree increments
    degrees = Math.round(degrees / 15) * 15;
  } else {
    // Snap to cardinal angles (0, 90, -90, 180) when within threshold
    const SNAP_THRESHOLD = 3;
    const snapAngles = [0, 90, -90, 180, -180];
    for (const snap of snapAngles) {
      if (Math.abs(degrees - snap) <= SNAP_THRESHOLD) {
        degrees = snap;
        break;
      }
    }
  }

  return degrees;
}

/**
 * Compute the initial angle offset when starting a rotation drag.
 */
export function computeStartAngle(
  nodeCenter: Point,
  mousePoint: Point,
  currentRotation: number,
): number {
  const angle = Math.atan2(
    mousePoint.y - nodeCenter.y,
    mousePoint.x - nodeCenter.x,
  );
  const degrees = (angle * 180) / Math.PI + 90;
  return degrees - currentRotation;
}

// ─── Cursor mapping ───────────────────────────────────────────────────────

const BASE_CURSORS: Record<Exclude<HandleId, 'rotate'>, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
};

/**
 * Get the CSS cursor string for a handle, accounting for node rotation.
 */
export function getCursorForHandle(handle: HandleId, rotation: number): string {
  if (handle === 'rotate') return 'grab';

  // Rotate the cursor based on the node rotation
  // Each 45-degree step shifts the cursor to the next direction
  const cursorOrder: string[] = [
    'ns-resize',      // 0
    'nesw-resize',    // 45
    'ew-resize',      // 90
    'nwse-resize',    // 135
    'ns-resize',      // 180
    'nesw-resize',    // 225
    'ew-resize',      // 270
    'nwse-resize',    // 315
  ];

  const handleAngles: Record<Exclude<HandleId, 'rotate'>, number> = {
    n: 0, ne: 45, e: 90, se: 135, s: 180, sw: 225, w: 270, nw: 315,
  };

  const totalAngle = (handleAngles[handle] + rotation + 360) % 360;
  const idx = Math.round(totalAngle / 45) % 8;
  return cursorOrder[idx];
}
